import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Button from '../Button';
import Col from '../Col';
import Form from '../Form';
import Input from '../Input';
import Picker from '../Picker';
import Row from '../Row';
import Toggle from '../Toggle';
import Toast from '../Toast/Toast';
import TextEditor from '../Editor/Editor';
import Checkbox from '../Checkbox';
import { selectFndFormData } from 'src/store/selectors/fndForm';
import { setFndFormData, clearFndFormData } from 'src/store/actions/fndForm';
import { repeatTypes, repeatEveryTypes } from 'src/common/constants';
import { fetchRepeatHours } from 'src/helpers/validationHelper';
import { variables } from 'src/Tenants';

interface MaintenanceGuideFormProps {
  onClose: () => void;
  breadcrumb?: string;
  onSaveMaintenanceGuide: (data: any) => void;
}

const MaintenanceGuideForm = ({
  onClose,
  breadcrumb,
  onSaveMaintenanceGuide,
}: MaintenanceGuideFormProps) => {
  const dispatch = useDispatch();
  const fndForm = useSelector((state: any) => selectFndFormData(state));

  const [editorState, setEditorState] = useState<string>(fndForm.body || '');
  const [buttonPressed, setButtonPressed] = useState<string>('');
  const repeatHours = fetchRepeatHours();

  const repeatTypeOption = {
    value: fndForm?.repeatType || 'Repeat',
    label: fndForm?.repeatType || 'Repeat',
  };

  const repeatEveryOption = fndForm?.repeatEvery
    ? { value: fndForm.repeatEvery, label: fndForm.repeatEvery }
    : null;

  const repeatTypeEveryOption = fndForm?.repeatTypeEvery
    ? { value: fndForm.repeatTypeEvery, label: fndForm.repeatTypeEvery }
    : null;

  const saveMaintenanceGuide = () => {
    let payload = { ...fndForm };

    // Clean up conditional data before saving
    if (payload.appointment) {
      if (payload.repeatType !== 'Custom') {
        delete payload.repeatEvery;
        delete payload.repeatTypeEvery;
      }
    } else {
      delete payload.repeatEvery;
      delete payload.repeatTypeEvery;
      delete payload.repeatType;
    }

    onSaveMaintenanceGuide(payload);

    if (buttonPressed === 'SaveAndAdd') {
      dispatch(clearFndFormData());
      setEditorState('');
    } else {
      onClose();
    }
  };

  const onEditorChange = (content: string) => {
    setEditorState(content);
    dispatch(setFndFormData({ body: content }));
  };

  const isButtonDisabled = () => {
    const {
      title,
      appointment,
      repeatType,
      IsSuiteWarrantyStartDate,
      repeatEvery,
      repeatTypeEvery,
    } = fndForm;

    if (!title || !editorState) return true;

    if (appointment) {
      const hasDate =
        IsSuiteWarrantyStartDate ||
        (appointment.date && !isNaN(new Date(appointment.date).getTime()));
      const hasTime =
        appointment.time && !isNaN(new Date(appointment.time).getTime());

      if (!hasDate || !hasTime) return true;
      if (repeatType === 'Custom' && (!repeatEvery || !repeatTypeEvery))
        return true;
      if (!repeatType) return true;
    }

    return false;
  };

  const handleToggleReminder = (isActive: boolean) => {
    const update = isActive
      ? { appointment: true, toggle: true }
      : {
          appointment: false,
          toggle: false,
          repeatEvery: '',
          repeatType: '',
          repeatTypeEvery: '',
          IsSuiteWarrantyStartDate: false,
        };
    dispatch(setFndFormData(update));
  };

  return (
    <Form
      headline={`${fndForm.id ? 'Edit' : 'Add'} a Maintenance Reminder`}
      onClose={onClose}
      onSubmit={saveMaintenanceGuide}
      actions={
        <>
          {!fndForm.id && (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              onClick={() => setButtonPressed('SaveAndAdd')}
              disabled={isButtonDisabled()}
            />
          )}
          <Button
            color="teal"
            size="small"
            label="Save Maintenance Reminder"
            onClick={() => setButtonPressed('SaveAndClose')}
            disabled={isButtonDisabled()}
          />
        </>
      }
      component={
        <Row mdCenter>
          <Col xs="12" md="12" lg="10">
            {breadcrumb && (
              <div style={{ marginBottom: '2rem' }}>
                <Toast
                  message={
                    <>
                      You are adding a Maintenance Reminder Note to{' '}
                      <strong>{breadcrumb}</strong>.
                    </>
                  }
                  actions={undefined}
                  color={undefined}
                />
              </div>
            )}
            <Input
              value={fndForm.title || ''}
              onChange={(e: any) =>
                dispatch(setFndFormData({ title: e.target.value }))
              }
              label="Subject"
              placeholder="Subject"
              block
            />

            <div className="input" style={{ marginTop: '1rem' }}>
              <div className="input__require">
                <label className="input__label">Description</label>
                <div className="input__required" />
              </div>
              <div
                style={{ borderBottom: '0.1rem solid rgba(83, 83, 83, 0.25)' }}
                className="input__control"
              >
                <TextEditor
                  editorState={editorState}
                  onEditorStateChange={onEditorChange}
                />
              </div>
            </div>
            <Toggle
              label="Set a Reminder"
              value={fndForm.appointment}
              onToggle={handleToggleReminder}
            />
            {fndForm.appointment && (
              <Row>
                <Col xs="12" md="12">
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ width: '20%' }}>
                      <Checkbox
                        label={`${variables.Suite} warranty start date`}
                        value={fndForm?.IsSuiteWarrantyStartDate}
                        onChange={() =>
                          dispatch(
                            setFndFormData({
                              IsSuiteWarrantyStartDate:
                                !fndForm.IsSuiteWarrantyStartDate,
                            })
                          )
                        }
                      />
                    </div>
                    {!fndForm.IsSuiteWarrantyStartDate && (
                      <Picker
                        minDate={new Date()}
                        placeholderText="Date"
                        selected={
                          fndForm.appointment?.date instanceof Date
                            ? fndForm.appointment.date
                            : null
                        }
                        onChange={(date: Date) =>
                          dispatch(
                            setFndFormData({
                              appointment: { ...fndForm.appointment, date },
                            })
                          )
                        }
                        label={undefined}
                        required={undefined}
                      />
                    )}
                    <Picker
                      placeholderText="Time"
                      selected={
                        fndForm.appointment?.time instanceof Date
                          ? fndForm.appointment.time
                          : null
                      }
                      onChange={(time: Date) =>
                        dispatch(
                          setFndFormData({
                            appointment: { ...fndForm.appointment, time },
                          })
                        )
                      }
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      dateFormat="h:mm aa"
                      label={undefined}
                      required={undefined}
                    />
                  </div>
                  <div
                    className="reminderWeekMob"
                    style={{ marginTop: '1rem' }}
                  >
                    <Row>
                      <Col xs="3">
                        <Select
                          menuPlacement="top"
                          value={repeatTypeOption}
                          onChange={(data: any) =>
                            dispatch(setFndFormData({ repeatType: data.value }))
                          }
                          options={repeatTypes.map((type: string) => ({
                            value: type,
                            label: type,
                          }))}
                        />
                      </Col>
                      {fndForm.repeatType === 'Custom' && (
                        <>
                          <Col
                            xs="2"
                            style={{ textAlign: 'center', lineHeight: '38px' }}
                          >
                            Repeat every
                          </Col>
                          <Col xs="2">
                            <Select
                              placeholder="Qty"
                              menuPlacement="top"
                              value={repeatEveryOption}
                              onChange={(data: any) =>
                                dispatch(
                                  setFndFormData({ repeatEvery: data.value })
                                )
                              }
                              options={repeatHours.map((type: any) => ({
                                value: type,
                                label: type,
                              }))}
                            />
                          </Col>
                          <Col xs="3">
                            <Select
                              menuPlacement="top"
                              value={repeatTypeEveryOption}
                              onChange={(data: any) =>
                                dispatch(
                                  setFndFormData({
                                    repeatTypeEvery: data.value,
                                  })
                                )
                              }
                              options={repeatEveryTypes.map((type: string) => ({
                                value: type,
                                label: type,
                              }))}
                            />
                          </Col>
                        </>
                      )}
                    </Row>
                  </div>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      }
      footer={undefined}
      strapline={undefined}
      onExit={undefined}
    />
  );
};

export default MaintenanceGuideForm;
